<template>
  <!--
    「点击音效与音量」卡片：
    - 顶部沿用 RtCard 提供的 icon / 标题 / 左右模式选择器；
    - 主体分两行：第一行按下声效 / 释放声效各占一半，第二行音量独占整行。
    - 每块都是 `bg-key rounded-4xl` 容器 + 自己的 title，分别承载一组按钮或一个 slider。
    - 模式选择由 RtCard 的 RtModeSelector 统一控制；父组件 RtBeepCard
      按 mode 取出该侧的值给子组件展示，写入时把 mode 透传给上层 setField，
      由其决定写左 / 写右 / 双写。
  -->
  <RtCard
    :mode="mode"
    :icon="RT_CONFIG_SECTION_META.sound.icon"
    :title="t('rt.tabs.sound')"
    :loading="loading"
    class="gap-3"
    @update:mode="(v) => emit('update:mode', v)"
  >
    <template #header-action>
      <RtModeSelector
        class="h-8"
        :model-value="mode"
        :disabled="loading || currentSoundValue === undefined"
        @update:model-value="(v: RtSelectionMode) => emit('update:mode', v)"
      />
    </template>

    <!--
      两列网格：音效和音量各占一列。音效选择会同时作用于按下和释放。
      gap-3 让块间留出与 DPI 档位区域相近的呼吸感；min-w-0 防子块溢出。
    -->
    <div class="flex flex-col gap-2 w-full h-full min-w-0">
      <RtSoundPicker
        :title="t('rt.sound.label')"
        :value="currentSoundValue"
        :min="soundConfig.min"
        :max="soundConfig.max"
        :disabled="loading || currentVolumeValue === undefined"
        class="col-span-1 min-w-0"
        @commit="(v: number) => onPickerCommit('beepPress', v)"
      />

      <RtVolumePicker
        :value="currentVolumeValue"
        :min="volumeConfig.min"
        :max="volumeConfig.max"
        :default-value="volumeConfig.default"
        :disabled="loading"
        class="col-span-1 min-w-0"
        @commit="(v: number) => onPickerCommit('beepVolume', v)"
      />
    </div>
  </RtCard>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  import RtCard from '../RtCard.vue'
  import RtModeSelector from '../RtModeSelector.vue'
  import RtSoundPicker from './RtSoundPicker.vue'
  import RtVolumePicker from './RtVolumePicker.vue'
  import { RT_CONFIG_SECTION_META, type RtSelectionMode } from '../../types'
  import type { MouseRTValueConfig, RtSide } from '@rapoo/device-sdk'

  type SideMap<T> = Partial<Record<RtSide, T>>

  const { t } = useI18n()

  const props = defineProps<{
    beep?: SideMap<number>
    beepVolume?: SideMap<number>
    soundConfig: MouseRTValueConfig
    volumeConfig: MouseRTValueConfig
    loading?: boolean
    mode: RtSelectionMode
  }>()

  const emit = defineEmits<{
    commit: [
      payload: {
        field: 'beepPress' | 'beepVolume'
        mode: RtSelectionMode
        value: number
      },
    ]
    'update:mode': [value: RtSelectionMode]
  }>()

  /**
   * 按 mode 取当前要展示给用户的单值：
   * - 单侧模式取该侧；
   * - both 模式以左侧为展示基准（与 RtVibrationCard 行为一致，避免双侧不同值时按钮来回闪烁）。
   * undefined 视为 0，确保按钮组总能高亮某一项而不是空白。
   */
  function pickValue(map?: SideMap<number>): number | undefined {
    if (props.mode === 'left') return map?.left
    if (props.mode === 'right') return map?.right
    return map?.left
  }

  const currentSoundValue = computed(() => pickValue(props.beep))
  const currentVolumeValue = computed(() => pickValue(props.beepVolume))

  /** 子组件回调统一封装：把 value 透传为 RtFieldKey 风格的 commit 事件 */
  function onPickerCommit(field: 'beepPress' | 'beepVolume', value: number) {
    emit('commit', { field, mode: props.mode, value })
  }
</script>
